
"use client";

import React, { useState } from "react";
import { FileUpload } from "./FileUpload";
import { JobInput } from "./JobInput";
import { ResultsView } from "./ResultsView";
import { Button } from "./ui/Button";
import { uploadAndGenerate, fetchDocument } from "../utils/api";
import { Loader2, Sparkles, AlertCircle, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { RecentResumes } from "./RecentResumes";
import { MOCK_RESULTS } from "../utils/mockData";
import { WelcomeCreditsPopup } from "./WelcomeCreditsPopup";
import { VideoWalkthrough } from "./VideoWalkthrough";
import { Play } from "lucide-react";
import { ResultsSkeleton } from "./ResultsSkeleton";

import { useAuth } from "../context/AuthContext";

export function Dashboard() {
  const useMockData = false;
  const [step, setStep] = useState<"input" | "processing" | "results">(useMockData ? "results" : "input");
  const [file, setFile] = useState<File | null>(null);
  const [jobDesc, setJobDesc] = useState("");
  const [rewriteMode, setRewriteMode] = useState("balanced");
  const [lockedSections, setLockedSections] = useState("");
  const [progress, setProgress] = useState({ step: "Initializing...", percent: 0 });
  const [results, setResults] = useState<any>(useMockData ? MOCK_RESULTS : null);
  const [error, setError] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  const { user, token, isLoading, refreshUser, showWelcomePopup, dismissWelcomePopup } = useAuth();

  // Clear state on logout
  React.useEffect(() => {
    if (!user) {
        setFile(null);
        setJobDesc("");
        setResults(null);
        setStep("input");
        setError(null);
    }
  }, [user]);
  
  // Load resume from URL parameter (when clicking from history page)
  React.useEffect(() => {
    if (typeof window !== 'undefined' && token) {
      const params = new URLSearchParams(window.location.search);
      const resumeId = params.get('resumeId');
      if (resumeId) {
        loadHistoricalResume(resumeId);
        // Keep resume ID in URL for state persistence
      }
    }
  }, [token]);
  
  // NOTE: Google Login and Payment logic moved to Navbar.tsx

  const canGenerate = file && jobDesc.length > 10;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    if (!token) {
        alert("Please sign in from the navigation bar to continue.");
        return;
    }
    
    setStep("processing");
    setError(null);
    
    // Parse locked sections from texarea (split by newlines)
    const lockedList = lockedSections.split("\n").map(s => s.trim()).filter(s => s.length > 0);
    
    try {
        const data = await uploadAndGenerate(file, jobDesc, rewriteMode, lockedList, (prog) => {
            setProgress(prog);
        }, token);
        await refreshUser(); // Refresh credits
        setResults(data);
        setStep("results");
    } catch (err: any) {
        setError(err.message || "Something went wrong");
        setStep("input");
    }
  };

  const loadHistoricalResume = async (id: string) => {
    if (!token) {
        alert("Please sign in to view resume history.");
        return;
    }
    
    setStep("processing");
    setProgress({ step: "Loading resume...", percent: 50 });
    setError(null);
    
    try {
        const data = await fetchDocument(id, token);
        setResults(data);
        setProgress({ step: "Done!", percent: 100 });
        setStep("results");
        // Update URL with resume ID
        window.history.pushState({}, '', `/?resumeId=${id}`);
    } catch (err: any) {
        setError(err.message || "Failed to load resume");
        setStep("input");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header Content */}
      {step === "input" &&
         (
         <div className="mb-12 text-center flex flex-col items-center">
         <h1 className="text-4xl md:text-7xl tracking-tight text-slate-900 mb-3">
            <span className="font-urbanist font-semibold">Resume</span> <span className="font-dancing-script font-bold text-blue-600">Aligned</span>
         </h1>
         <p className="text-sm text-slate-500 mb-4 max-w-2xl">
            Optimize your resume for any job description using AI. Get a tailored resume and cover letter in seconds.
         </p>
         
         <button
            onClick={() => setShowVideo(true)}
            className="group flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-all text-sm font-medium"
         >
            <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play size={14} fill="currentColor" className="ml-0.5" />
            </span>
            Watch Demo
         </button>
      </div>
      )}

      {step === "results" && (
         <div className="mb-8">
            <Link 
              href="/" 
              className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors font-medium group"
              onClick={(e) => {
                e.preventDefault();
                setStep("input");
                setResults(null);
                window.history.pushState({}, '', '/');
              }}
            >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <span>Back to Editor</span>
            </Link>
         </div>
      )}

       <AnimatePresence mode="wait">
        {step === "input" && (
          <motion.div
             key="input"
             initial={{ opacity: 0, y: 10 }}
             animate={{ opacity: 1, y: 0 }}
             exit={{ opacity: 0, y: -10 }}
             className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
             <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                   <FileUpload selectedFile={file} onFileSelect={setFile} />
                </div>
             </div>
             
             <div className="space-y-6">
                 <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-full flex flex-col gap-6">
                    <JobInput value={jobDesc} onChange={setJobDesc} />
                    
                    {/* <div className="grid grid-cols-2 gap-4 border-t pt-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Rewrite Mode</label>
                            <select 
                                value={rewriteMode}
                                onChange={(e) => setRewriteMode(e.target.value)}
                                className="w-full rounded-md border-slate-300 text-sm p-2 bg-slate-50 border"
                            >
                                <option value="conservative">Conservative (Minimal)</option>
                                <option value="balanced">Balanced (Default)</option>
                                <option value="aggressive">Aggressive (ATS Focused)</option>
                            </select>
                        </div>
                        <div>
                             <label className="block text-sm font-medium text-slate-700 mb-2">Locked Sections</label>
                             <textarea 
                                value={lockedSections}
                                onChange={(e) => setLockedSections(e.target.value)}
                                placeholder="Summary, Education..." 
                                className="w-full rounded-md border-slate-300 text-sm p-2 bg-slate-50 border h-[42px] min-h-[42px] resize-none overflow-hidden"
                                title="Enter section names to lock, separated by newlines"
                             />
                        </div>
                    </div> */}
                 </div>
             </div>
             
             <div className="md:col-span-2 flex justify-center mt-4">
                <div className="flex flex-col items-center gap-4 w-full max-w-md">
                    {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg w-full">
                            <AlertCircle className="h-4 w-4" />
                            {error}
                        </div>
                    )}
                    
                    {/* Email Verification Required */}
                    {user && !user.emailVerified ? (
                        <div className="text-center w-full bg-orange-50 p-4 rounded-lg border border-orange-100">
                            <p className="text-orange-800 font-medium mb-2">Email Verification Required</p>
                            <p className="text-sm text-orange-600 mb-3">
                                Please verify your email address to generate resumes. Check your inbox for the verification link.
                            </p>
                            <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={async () => {
                                    const result = await refreshUser();
                                    if (user?.emailVerified) {
                                        alert("Email verified! You can now generate resumes.");
                                    }
                                }}
                                className="text-orange-600 border-orange-300 hover:bg-orange-100"
                            >
                                I've Verified - Refresh
                            </Button>
                        </div>
                    ) : user && user.credits <= 0 ? (
                        <div className="text-center w-full bg-orange-50 p-4 rounded-lg border border-orange-100">
                            <p className="text-orange-800 font-medium mb-2">Out of credits</p>
                            <p className="text-sm text-orange-600">Please buy credits from the navigation bar.</p>
                        </div>
                    ) : (
                        <Button 
                            size="lg" 
                            onClick={handleGenerate} 
                            disabled={!canGenerate || (user ? !user.emailVerified : false)}
                            className="w-full text-sm shadow-lg shadow-slate-900/10 rounded-full border-2 border-slate-500"
                        >
                            {user ? "Generate" : "Sign In to Generate"}
                        </Button>
                    )}
                </div>
             </div>
          </motion.div>
        )}

        {step === "processing" && (
           <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full"
           >
              <ResultsSkeleton />
              
              {/* Optional: Overlay progress text if needed, or just let skeleton do the work */}
              <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
                  <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-slate-100 flex flex-col items-center">
                        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
                        <h2 className="text-lg font-semibold text-slate-900">{progress.step}</h2>
                        <p className="text-sm text-slate-500">{progress.percent}%</p>
                  </div>
              </div>
           </motion.div>
        )}

        {step === "results" && results && (
            <motion.div
               key="results"
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               className=""
            >
               <ResultsView data={results} />
               <div className="mt-6 text-center text-gray-500">
                  <Button variant="outline" onClick={() => {
                    setStep("input");
                    setJobDesc('');
                    setResults(null);
                    window.history.pushState({}, '', '/');
                  }}>Start Over</Button>
               </div>
            </motion.div>
        )}
       </AnimatePresence>

       {step === "input" && <RecentResumes onResumeClick={loadHistoricalResume} />}
       
       {/* Welcome Credits Popup */}
       <WelcomeCreditsPopup 
         isOpen={showWelcomePopup} 
         onClose={dismissWelcomePopup}
         credits={3}
       />
       
       {/* Video Walkthrough Modal */}
       <VideoWalkthrough 
         isOpen={showVideo} 
         onClose={() => setShowVideo(false)} 
       />
    </div>
  );
}
